import { ConfigProvider, Menu } from 'antd';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useTerminology from '@/common/hooks/useTerminology';
import { useSettingsStore } from '@core/settings/store/settings';

const HeaderMenu = () => {
  const { t } = useTranslation();
  const { isDayPassEnabled, isMeetingRoomEnabled, isPackagesEnabled, isUnitsEnabled, textColor } = useSettingsStore();
  const { meetingRoomTerminology } = useTerminology();
  const router = useRouter();
  const clientName = router.query.clientName as string;

  const memoizedMenuItems = useMemo(() => {
    const menuItems = [{ key: '/home', label: t('nav.home') }];
    if (isUnitsEnabled) {
      menuItems.push({ key: '/rentals/office-type', label: t('nav.availableRentals') });
    }
    if (isMeetingRoomEnabled) {
      menuItems.push({
        key: '/booking/meeting-room',
        label: t('nav.bookMeetingRoom', { terminology: meetingRoomTerminology }),
      });
    }
    if (isDayPassEnabled) {
      menuItems.push({
        key: '/booking/day-pass',
        label: t('nav.buyDayPass'),
      });
    }
    if (isPackagesEnabled) {
      menuItems.push({ key: '/booking/packages', label: t('nav.buyCredits') });
    }
    return menuItems;
  }, [isDayPassEnabled, isMeetingRoomEnabled, isPackagesEnabled, isUnitsEnabled, meetingRoomTerminology, t]);

  const onMenuItemClick = useCallback(
    ({ key }) => {
      router.push(`${key}?clientName=${clientName || 'demo'}`);
    },
    [clientName, router],
  );

  const activeMenuItem = useMemo(() => {
    const active = memoizedMenuItems.find((item) => {
      return router.pathname.includes(item.key);
    });
    return active?.key;
  }, [memoizedMenuItems, router.pathname]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            activeBarHeight: 0,
            itemPaddingInline: 25,
            colorBgContainer: 'transparent',
            itemColor: textColor,
            itemHoverColor: textColor,
            itemSelectedColor: textColor,
          },
        },
      }}
    >
      <Menu
        style={{ flex: 'auto' }}
        className="h-[78px] min-w-0 flex w-full items-center justify-end flex-row font-sans text-lg font-semibold"
        mode="horizontal"
        items={memoizedMenuItems}
        selectedKeys={[activeMenuItem]}
        onClick={onMenuItemClick}
      />
    </ConfigProvider>
  );
};

export default HeaderMenu;
